import { useCallback } from "react";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type {
  ArticleRecirculationWidgetClickEventParameters,
  ArticleRecirculationWidgetImpEventParameters,
  RecirculationWidgetImpressionEvent,
} from "scmp-app/components/tracking/google-analytics-4/types";
import { useOptimizelyContext } from "scmp-app/lib/optimizely";

type Parameters = {
  getClickCustomizedParameters: (
    entityId?: string,
    urlAlias?: string,
  ) => ArticleRecirculationWidgetClickEventParameters;
  getImpressionCustomizedParameters: () => ArticleRecirculationWidgetImpEventParameters;
};

export const useTracking = ({
  getClickCustomizedParameters,
  getImpressionCustomizedParameters,
}: Parameters) => {
  const { fireEvent } = useOptimizelyContext();

  const getImpressionGa4Event = useCallback(
    () =>
      ({
        action: "imp",
        category: "recirculation",
        customized_parameters: getImpressionCustomizedParameters(),
        subcategory: "widget",
      }) satisfies RecirculationWidgetImpressionEvent,
    [getImpressionCustomizedParameters],
  );

  const { captureTrackImpressionEventTargetElement: captureImpressionTrackingElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: getImpressionGa4Event,
      options: {
        intersectionThreshold: [0.1],
        isSendGA4Tracking: true,
        shouldSendOnce: true,
        shouldThrottle: true,
      },
    });

  const handleClickTracking = useCallback(
    (entityId?: string, urlAlias?: string) => {
      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: getClickCustomizedParameters(entityId, urlAlias),
        subcategory: "widget",
      });
      fireEvent?.({
        eventName: "item_click",
      });
    },
    [fireEvent, getClickCustomizedParameters],
  );

  return {
    captureImpressionTrackingElement,
    handleClickTracking,
  };
};
